<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section id="doc-crop-tool" class="first-screen">
      <div class="breadcrumb">
        <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
        <span>&rsaquo;</span>
        <router-link to="/tools">{{ $t('tools.page.allTools') }}</router-link>
        <span>&rsaquo;</span>
        <span>{{ $t('tools.doc-crop-enhance.name') }}</span>
      </div>

      <div class="first-screen-grid">
        <div class="action-column">
      <div v-if="!isLoggedIn" class="login-prompt">
        <div class="login-prompt-content">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <h2>{{ $t('tools.detail.loginRequired') }}</h2>
          <p>{{ $t('tools.detail.loginRequiredDesc') }}</p>
          <button class="btn btn-primary" @click="goLogin">{{ $t('brandSeo.toolFreeTrialCta') }}</button>
        </div>
      </div>

      <div v-else class="tool-interface">
        <div
          class="tool-upload"
          @click="triggerUpload"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleFile">
          <div v-if="!uploaded" class="upload-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <p>{{ $t('tools.detail.clickUpload') }}{{ $t('tools.fileTypes.image') }}</p>
            <span>{{ $t('tools.detail.support') }} JPG, PNG, WebP</span>
          </div>
          <div v-else class="upload-preview">
            <div class="preview-box preview-box--image">
              <img v-if="previewSrc" :src="previewSrc" :alt="$t('tools.doc-crop-enhance.name') + ' preview'" class="preview-img clickable-preview" @click.stop="openImage(previewSrc)">
            </div>
            <button class="btn btn-secondary" @click.stop="clearUpload">{{ $t('tools.detail.reupload') }}</button>
          </div>
        </div>

        <div class="tool-options">
          <div class="option-controls">
          <div class="option-group">
            <label class="option-label">{{ $t('tools.doc-crop-enhance.enhanceMode') }}</label>
            <div class="option-control">
              <select v-model="enhanceMode" class="form-select">
                <option :value="-1">{{ $t('tools.doc-crop-enhance.enhanceModes.disabled') }}</option>
                <option :value="1">{{ $t('tools.doc-crop-enhance.enhanceModes.brighten') }}</option>
                <option :value="2">{{ $t('tools.doc-crop-enhance.enhanceModes.sharpen') }}</option>
                <option :value="3">{{ $t('tools.doc-crop-enhance.enhanceModes.blackWhite') }}</option>
                <option :value="4">{{ $t('tools.doc-crop-enhance.enhanceModes.grayscale') }}</option>
                <option :value="5">{{ $t('tools.doc-crop-enhance.enhanceModes.deshadow') }}</option>
                <option :value="6">{{ $t('tools.doc-crop-enhance.enhanceModes.bitmap') }}</option>
              </select>
            </div>
          </div>
          <div class="option-group">
            <label class="option-label">{{ $t('tools.doc-crop-enhance.cropImage') }}</label>
            <div class="option-control">
              <label class="switch">
                <input type="checkbox" v-model="cropImageEnabled">
                <span class="switch-slider"></span>
              </label>
            </div>
          </div>
          <div class="option-group">
            <label class="option-label">{{ $t('tools.doc-crop-enhance.dewarpImage') }}</label>
            <div class="option-control">
              <label class="switch">
                <input type="checkbox" v-model="dewarpImageEnabled">
                <span class="switch-slider"></span>
              </label>
            </div>
          </div>
          </div>
          <div class="tool-actions">
          <button class="btn btn-primary btn-lg" :disabled="!uploaded || processing" @click="process">
            <svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span v-else>{{ $t('brandSeo.toolProcessCta', { action: $t('tools.detail.process') }) }}</span>
          </button>
          </div>
        </div>
      </div>
        </div>

      <div class="showcase-column">
      <div class="tool-hero">
        <div class="tool-hero-info">
          <span class="tool-icon-lg">📄</span>
          <h1>{{ $t('tools.doc-crop-enhance.name') }}</h1>
          <p class="tool-hero-desc">{{ $t('brandSeo.toolDescription', { description: $t('tools.doc-crop-enhance.fullDesc') }) }}</p>
        </div>
      </div>

      <div class="demo-section workspace-panel" v-if="!processing && !processed">
        <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
        <div class="demo-comparison">
          <div class="demo-item">
            <span class="demo-label">{{ $t('tools.detail.before') }}</span>
            <div class="demo-image">
              <img src="@/assets/images/doc-crop-before.png" alt="Before" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)">
            </div>
          </div>
          <div class="demo-arrow">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div class="demo-item">
            <span class="demo-label">{{ $t('tools.detail.after') }}</span>
            <div class="demo-image">
              <img src="@/assets/images/doc-crop-after.png" alt="After" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)">
            </div>
          </div>
        </div>
      </div>

      <div class="processing-panel workspace-panel" v-else-if="processing">
        <div class="workspace-heading">
          <h2>{{ $t('tools.detail.processing') }}</h2>
          <span class="processing-status"><i></i>{{ $t('tools.doc-crop-enhance.name') }}</span>
        </div>
        <div class="processing-preview">
          <img v-if="previewSrc" :src="previewSrc" alt="Processing document">
          <div class="processing-overlay">
            <div class="loading-spinner"></div>
            <strong>{{ $t('tools.detail.processing') }}</strong>
          </div>
        </div>
        <div class="processing-track"><span></span></div>
      </div>

      <div class="result-panel workspace-panel" v-else>
        <div class="workspace-heading">
          <h2>{{ $t('tools.detail.after') }}</h2>
          <span class="result-status">✓ {{ $t('tools.doc-crop-enhance.name') }}</span>
        </div>
        <div class="output-comparison">
          <div class="output-before">
            <span class="output-label">{{ $t('tools.detail.before') }}</span>
            <div class="output-image output-image--fill">
              <img v-if="previewSrc" :src="previewSrc" alt="Before" class="compare-img clickable-preview" @click.stop="openImage(previewSrc)">
            </div>
          </div>
          <div class="output-after">
            <span class="output-label">{{ $t('tools.detail.after') }}</span>
            <div class="output-image output-image--fill">
              <img v-if="resultSrc" :src="resultSrc" alt="After" class="compare-img clickable-preview" @click.stop="openImage(resultSrc)">
            </div>
          </div>
        </div>
        <div class="output-actions">
          <button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">⬇️ {{ $t('tools.detail.download') }}</button>
          <button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button>
        </div>
      </div>
      </div>
      </div>
      </section>

      <div class="seo-content-start detail-section feature-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.doc-crop-enhance.featuresKicker') }}</span>
          <h2>{{ $t('brandSeo.sectionTitle', { title: $t('tools.doc-crop-enhance.featuresTitle') }) }}</h2>
          <p>{{ $t('tools.doc-crop-enhance.featuresIntro') }}</p>
        </div>
        <a v-if="features[0]" class="section-primary-cta" href="#doc-crop-tool">{{ features[0].cta }}</a>
        <div class="landing-demo-comparison">
          <div class="landing-demo-half"><img src="@/assets/images/doc-crop-before.png" alt="Before" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)"></div>
          <div class="landing-demo-half"><img src="@/assets/images/doc-crop-after.png" alt="After" class="clickable-preview" @click.stop="openImage($event.currentTarget.src)"></div>
          <span class="landing-demo-divider"></span>
        </div>
        <div class="feature-list">
          <article class="feature-row" v-for="(feature, idx) in features" :key="'feature-' + idx">
            <div class="feature-visual">
              <div class="feature-visual-half"><img :src="featureImages[idx].before" :alt="featureImages[idx].label + ' before'" class="clickable-preview" @click.stop="openImage(featureImages[idx].before)"></div>
              <div class="feature-visual-half"><img :src="featureImages[idx].after" :alt="featureImages[idx].label + ' after'" class="clickable-preview" @click.stop="openImage(featureImages[idx].after)"></div>
              <span class="feature-divider"></span>
            </div>
            <div class="feature-copy">
              <span class="feature-index">0{{ idx + 1 }}</span>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.desc }}</p>
              <a class="feature-cta" href="#doc-crop-tool">{{ feature.cta }}</a>
            </div>
          </article>
        </div>
      </div>

      <div class="detail-section education-section">
        <div class="education-heading">
          <span class="education-kicker">{{ $t('tools.doc-crop-enhance.education.kicker') }}</span>
          <h2>{{ $t('tools.doc-crop-enhance.education.title') }}</h2>
          <p>{{ $t('tools.doc-crop-enhance.education.intro') }}</p>
        </div>
        <div class="education-grid">
          <article v-for="(item, idx) in educationItems" :key="'education-' + idx" class="education-card">
            <span>0{{ idx + 1 }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
        <div class="education-related">
          <div>
            <h3>{{ $t('tools.doc-crop-enhance.education.relatedTitle') }}</h3>
            <p>{{ $t('tools.doc-crop-enhance.education.relatedDesc') }}</p>
          </div>
          <router-link
            class="btn btn-secondary"
            :to="{ name: 'HandwrittenEraseDetail', params: { lang: $route.params.lang } }"
          >{{ $t('tools.doc-crop-enhance.education.relatedCta') }}</router-link>
        </div>
      </div>

      <div class="detail-section use-case-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.doc-crop-enhance.useCasesKicker') }}</span>
          <h2>{{ $t('tools.doc-crop-enhance.useCasesTitle') }}</h2>
          <p>{{ $t('tools.doc-crop-enhance.useCasesIntro') }}</p>
        </div>
        <div class="use-case-grid">
          <article v-for="(item, idx) in useCases" :key="'case-' + idx" class="use-case-card">
            <span>0{{ idx + 1 }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>

      <div class="detail-section" style="margin-top:64px;">
        <h2>{{ $t('brandSeo.sectionTitle', { title: $t('tools.detail.howToUse') }) }}</h2>
        <div class="steps">
          <div class="step" v-for="(step, idx) in steps" :key="idx">
            <div class="step-number">{{ idx + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>

      <div class="detail-section more-tools-section">
        <h2>{{ $t('tools.detail.moreTools') }}</h2>
        <div class="grid-5">
          <ToolListCard v-for="t in otherTools" :key="t.id" :tool-id="t.id" :icon="t.icon" :badge="t.badge" :to="'/tools/' + t.id" />
        </div>
      </div>

      <div class="detail-section faq-section">
        <div class="section-heading">
          <span class="section-kicker">FAQ</span>
          <h2>{{ $t('brandSeo.faqTitle', { title: $t('tools.doc-crop-enhance.faqTitle') }) }}</h2>
        </div>
        <div class="faq-list">
          <details v-for="(item, idx) in faqs" :key="'faq-' + idx" class="faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>

      <ToolHowToCta
        :kicker="$t('tools.detail.howToUse')"
        :title="$t('brandSeo.sectionTitle', { title: $t('tools.detail.howToUse') })"
        :intro="$t('tools.doc-crop-enhance.fullDesc')"
        :steps="steps"
        :cta-label="$t('brandSeo.toolFreeTrialCta')"
        target-id="doc-crop-tool"
      />

    </div>
    <ImageLightbox :src="lightboxSrc" @close="closeImage" />
  </div>
</template>

<script>
import { cropEnhanceImage } from '@/api/textin'
import { uploadFile, saveOrUpdateAIHistory, formatCreateDate } from '@/api'
import { isAuthenticated } from '@/api/auth'
import ImageLightbox from '@/components/ImageLightbox.vue'
import ToolHowToCta from '@/components/ToolHowToCta.vue'

var MAX_FILE_BYTES = 15 * 1024 * 1024
var DEFAULT_FEATURE_IMAGE = {
  label: 'Document crop and correction',
  before: require('@/assets/images/doc-crop-before.png'),
  after: require('@/assets/images/doc-crop-after.png')
}
var FEATURE_IMAGES = [
  { label: 'Automatic document crop', before: require('@/assets/images/doc-crop-examples/crop-before.webp'), after: require('@/assets/images/doc-crop-examples/crop-after.webp') },
  DEFAULT_FEATURE_IMAGE,
  { label: 'Curved page dewarp', before: require('@/assets/images/doc-crop-examples/dewarp-before.webp'), after: require('@/assets/images/doc-crop-examples/dewarp-after.webp') },
  { label: 'Document shadow removal', before: require('@/assets/images/doc-crop-examples/deshadow-before.webp'), after: require('@/assets/images/doc-crop-examples/deshadow-after.webp') },
  { label: 'Scanned text sharpening', before: require('@/assets/images/doc-crop-examples/sharpen-before.webp'), after: require('@/assets/images/doc-crop-examples/sharpen-after.webp') },
  DEFAULT_FEATURE_IMAGE
]

function b64ToPngFile(b64Str, index) {
  var raw = b64Str.replace(/\s/g, '')
  var pad = raw.length % 4
  if (pad) raw += '===='.slice(0, 4 - pad)
  var binStr = atob(raw)
  var len = binStr.length
  var bytes = new Uint8Array(len)
  for (var i = 0; i < len; i++) bytes[i] = binStr.charCodeAt(i)
  var name = 'doc-crop-enhanced-' + Date.now() + '-' + index + '.png'
  return new File([bytes], name, { type: 'image/png' })
}

function dataURLtoUint8Array(dataUrl) {
  var base64 = dataUrl.split(',')[1]
  var binaryString = atob(base64)
  var bytes = new Uint8Array(binaryString.length)
  for (var i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

export default {
  name: 'DocCropEnhanceDetail',
  components: {
    ImageLightbox: ImageLightbox,
    ToolHowToCta: ToolHowToCta
  },
  data() {
    return {
      uploaded: false,
      processing: false,
      processed: false,
      previewSrc: '',
      resultSrc: '',
      lightboxSrc: '',
      uploadedFile: null,
      processError: null,
      enhanceMode: -1,
      cropImageEnabled: true,
      dewarpImageEnabled: true,
      featureImages: FEATURE_IMAGES
    }
  },
  mounted() {
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    steps() {
      return [
        { title: this.$t('tools.steps.upload.title'), desc: this.$t('tools.steps.upload.desc') },
        { title: this.$t('tools.steps.process.title'), desc: this.$t('tools.steps.process.desc') },
        { title: this.$t('tools.steps.download.title'), desc: this.$t('tools.steps.download.desc') }
      ]
    },
    educationItems() {
      return this.$t('tools.doc-crop-enhance.education.items')
    },
    features() {
      return this.$t('tools.doc-crop-enhance.features')
    },
    useCases() {
      return this.$t('tools.doc-crop-enhance.useCases')
    },
    // 合并工具专属问答与 Miral AI 免费试用说明。
    faqs() {
      var toolFaqs = this.$t('tools.doc-crop-enhance.faqs')
      var brandFaqs = this.$t('brandSeo.toolFaqs')
      return (Array.isArray(toolFaqs) ? toolFaqs : []).concat(Array.isArray(brandFaqs) ? brandFaqs : [])
    },
    otherTools() {
      return this.$store.state.tools.filter(function(t) {
        return t.id !== 'doc-crop-enhance'
      }).slice(0, 5)
    }
  },
  methods: {
    // 打开图片全屏预览。
    openImage: function(src) {
      this.lightboxSrc = src || ''
    },
    // 关闭图片全屏预览。
    closeImage: function() {
      this.lightboxSrc = ''
    },
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    triggerUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    onDrop(e) {
      var files = e.dataTransfer && e.dataTransfer.files
      if (files && files[0]) this.readFile(files[0])
    },
    handleFile(e) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) {
        if (input) input.value = ''
        return
      }
      this.readFile(file)
      if (input) input.value = ''
    },
    readFile(file) {
      var self = this
      this.processError = null
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.processError = this.$t('tools.doc-crop-enhance.notImageFile')
        return
      }
      if (file.size > MAX_FILE_BYTES) {
        this.processError = this.$t('tools.doc-crop-enhance.fileTooLarge')
        return
      }
      var reader = new FileReader()
      reader.onload = function(ev) {
        self.previewSrc = (ev.target && ev.target.result) ? String(ev.target.result) : ''
        self.uploadedFile = file
        self.uploaded = !!self.previewSrc
        self.processed = false
        self.resultSrc = ''
      }
      reader.onerror = function() {
        self.processError = self.$t('common.generateFailedContactSupport')
      }
      reader.readAsDataURL(file)
    },
    clearUpload() {
      this.uploaded = false
      this.previewSrc = ''
      this.uploadedFile = null
      this.resultSrc = ''
      this.processed = false
      this.processing = false
      this.processError = null
    },
    reset() {
      this.clearUpload()
    },
    downloadResult() {
      if (!this.resultSrc) return
      var url = this.resultSrc
      var name = this.$t('tools.doc-crop-enhance.downloadName')
      fetch(url)
        .then(function(r) { return r.blob() })
        .then(function(blob) {
          var u = URL.createObjectURL(blob)
          var a = document.createElement('a')
          a.href = u
          a.download = name
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(u)
        })
        .catch(function() {
          window.open(url, '_blank')
        })
    },
    process() {
      var self = this
      if (!this.previewSrc || !this.uploadedFile) return
      this.processing = true
      this.processed = false
      this.processError = null

      var createDate = formatCreateDate(new Date())
      var toolId = 'doc-crop-enhance'
      var aiName = this.$t('tools.' + toolId + '.name')
      var aiDesc = this.$t('tools.' + toolId + '.desc')
      var historyId = null
      var originalUrl = ''

      // 1. 先上传原图到服务器
      uploadFile(this.uploadedFile).then(function(url) {
        originalUrl = url
      }).catch(function() {
        self.processing = false
        self.processError = self.$t('tools.detail.uploadOriginalFailed')
        return Promise.reject('uploadOriginalFailed')
      }).then(function() {
        // 2. 创建历史记录，带上原图URL
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      }).then(function() {
        // 3. 调第三方API处理
        var imageData = dataURLtoUint8Array(self.previewSrc)
        var options = {
          enhanceMode: self.enhanceMode,
          cropImage: self.cropImageEnabled ? 1 : 0,
          dewarpImage: self.dewarpImageEnabled ? 1 : 0,
          deblurImage: 0,
          onlyPosition: 0
        }
        return cropEnhanceImage(imageData, options)
      }).then(function(res) {
        var data = res && res.data
        var imageList = data && data.result && data.result.image_list
        var firstItem = imageList && imageList[0]
        var b64Image = firstItem && firstItem.image
        if (data && data.code === 200 && b64Image) {
          var file = b64ToPngFile(b64Image, 0)
          return uploadFile(file).then(function(serverUrl) {
            self.processing = false
            self.resultSrc = serverUrl
            self.processed = true
            self.processError = null
            // 4. 更新历史记录（成功）
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: '',
              aiDesc: aiDesc,
              state: 2,
              aiResultUrl: serverUrl,
              image_large_url: originalUrl,
              aiMediaType: 'image',
              createDate: createDate
            }).catch(function() {})
          })
        } else {
          self.processing = false
          self.resultSrc = ''
          self.processed = false
          self.processError = self.$userFacingError(data || res, self.$t('common.generateFailedContactSupport'))
          saveOrUpdateAIHistory({
            aiName: aiName,
            historyId: historyId,
            taskId: '',
            aiDesc: aiDesc,
            state: 3,
            message: (typeof error !== 'undefined' && error && error.message) ? error.message : ((typeof err !== 'undefined' && err && err.message) ? err.message : ((typeof statusRes !== 'undefined' && statusRes && statusRes.error) ? statusRes.error : '')),
            aiResultUrl: '',
            image_large_url: originalUrl,
            aiMediaType: 'image',
            createDate: createDate
          }).catch(function() {})
        }
      }).catch(function(err) {
        if (err === 'uploadOriginalFailed') return
        self.processing = false
        self.processed = false
        self.resultSrc = ''
        self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc,
          state: 3,
          message: (typeof err !== 'undefined' && err && err.message) ? err.message : '',
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
      })
    }
  }
}
</script>

<style scoped>
.tool-detail-page { padding: 48px 0 80px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; margin-bottom: 32px; font-size: 13px; color: var(--text-muted); }
.breadcrumb a { color: var(--text-secondary); }
.breadcrumb a:hover { color: var(--accent); }
.tool-hero { margin-bottom: 32px; }
.tool-hero-info { text-align: center; max-width: 640px; margin: 0 auto; }
.tool-icon-lg { font-size: 48px; display: block; margin-bottom: 16px; }
.tool-hero-info h1 { font-size: 36px; font-weight: 800; margin-bottom: 12px; }
.tool-hero-desc { font-size: 16px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px; }
.login-prompt { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 24px 28px; text-align: center; margin: 32px auto 0; max-width: 680px; }
.login-prompt-content { max-width: 360px; margin: 0 auto; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; font-size: 20px; }
.login-prompt p { color: var(--text-muted); font-size: 14px; margin-bottom: 12px; }
.tool-interface { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 32px; margin: 32px auto 0; max-width: 680px; }
.tool-upload { cursor: pointer; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px; border: 2px dashed var(--border-color); border-radius: var(--radius-lg); transition: border-color 0.2s; }
.upload-placeholder:hover { border-color: var(--accent); }
.upload-placeholder p { margin-top: 12px; font-size: 14px; color: var(--text-secondary); }
.upload-placeholder span { font-size: 12px; color: var(--text-muted); }
.upload-preview { display: flex; flex-direction: column; align-items: center; gap: 16px; max-height: none; }
.preview-box { width: 360px; max-width: 100%; aspect-ratio: 4 / 3; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; overflow: hidden; background: #e8e8ec; }
.preview-box--image { padding: 0; }
.preview-img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; display: block; }
.clickable-preview { cursor: zoom-in; }
.tool-error { color: #f87171; font-size: 13px; margin: 0 0 12px; }
.tool-error--block { text-align: center; margin-bottom: 16px; }
.output-image--fill { padding: 0; overflow: hidden; background: var(--bg-primary); }
.compare-img { width: 100%; height: 100%; object-fit: contain; display: block; min-height: 200px; max-height: 420px; background: var(--bg-primary); }
.tool-options { display: flex; flex-direction: column; gap: 16px; margin-top: auto; padding-top: 12px; }
.option-controls { display: flex; align-items: flex-start; justify-content: center; gap: 16px; flex-wrap: wrap; }
.tool-actions { width: 100%; }
.tool-actions .btn { width: 100%; justify-content: center; text-align: center; }
.tool-actions .btn:disabled { cursor: not-allowed; opacity: .48; }
.option-group { display: grid; grid-template-rows: 20px 40px; gap: 6px; align-items: start; }
.option-group > .option-label { margin: 0; font-size: 12px; color: var(--text-muted); font-weight: 500; line-height: 20px; }
.option-control { display: flex; align-items: center; height: 40px; }
.option-control > .switch { margin: 0; }
.form-select { padding: 10px 16px; background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: var(--radius-md); color: var(--text-primary); font-size: 14px; font-family: inherit; outline: none; min-width: 140px; cursor: pointer; }
.form-select:focus { border-color: var(--accent); }
.switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.switch-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: var(--border-color); transition: 0.3s; border-radius: 24px; }
.switch-slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: 0.3s; border-radius: 50%; }
.switch input:checked + .switch-slider { background-color: var(--accent); }
.switch input:checked + .switch-slider:before { transform: translateX(20px); }
.tool-result { margin-top: 32px; }
.gen-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.output-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; }
.output-image { aspect-ratio: 1; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; }
.output-actions { display: flex; gap: 12px; justify-content: center; margin-top: 12px; }
.demo-section { margin-top: 32px; padding: 24px; background: var(--bg-primary); border-radius: var(--radius-lg); }
.demo-title { font-size: 16px; font-weight: 600; text-align: center; margin-bottom: 20px; color: var(--text-primary); }
.demo-comparison { display: flex; align-items: center; justify-content: center; gap: 24px; flex-wrap: wrap; }
.demo-item { flex: 1; min-width: 200px; max-width: 300px; }
.demo-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; text-align: center; }
.demo-image { aspect-ratio: 4/3; border-radius: var(--radius-lg); overflow: hidden; background: var(--bg-secondary); }
.demo-image img { width: 100%; height: 100%; object-fit: cover; }
.demo-arrow { color: var(--text-muted); flex-shrink: 0; }
.detail-section { margin-bottom: 64px; }
.detail-section h2 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
.feature-section { margin-top: 104px; }
.section-heading { max-width: 780px; margin: 0 auto 48px; text-align: center; }
.section-heading h2 { margin: 8px 0 14px; font-size: clamp(30px, 4vw, 44px); line-height: 1.2; }
.section-heading p { color: var(--text-secondary); line-height: 1.8; }
.section-kicker { color: #07977d; font-size: 13px; font-weight: 800; letter-spacing: .08em; }
.feature-list { display: flex; flex-direction: column; gap: 100px; }
.feature-row { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr); align-items: center; gap: 72px; }
.feature-row:nth-child(even) .feature-visual { order: 2; }
.feature-row:nth-child(even) .feature-copy { order: 1; }
.feature-visual { position: relative; display: grid; grid-template-columns: 1fr 1fr; height: 390px; overflow: hidden; border-radius: 24px; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.feature-visual-half { min-width: 0; overflow: hidden; background: #eef2f4; }
.feature-visual-half img { width: 100%; height: 100%; object-fit: cover; }
.feature-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .92); }
.feature-copy { max-width: 440px; }
.feature-index { color: #07977d; font-size: 13px; font-weight: 800; letter-spacing: .12em; }
.feature-copy h3 { margin: 14px 0 16px; font-size: clamp(28px, 3vw, 40px); line-height: 1.18; }
.feature-copy p { margin-bottom: 26px; color: var(--text-secondary); font-size: 16px; line-height: 1.8; }
.feature-cta { display: inline-flex; align-items: center; min-height: 46px; padding: 0 24px; border: 1px solid #07977d; border-radius: 999px; color: #087d68; font-size: 14px; font-weight: 750; }
.feature-cta:hover { color: #fff; background: #07977d; }
.education-section { margin-top: 96px; padding: 56px; border-radius: 28px; background: #10282b; color: #fff; }
.education-heading { max-width: 760px; margin: 0 auto 36px; text-align: center; }
.education-heading h2 { margin: 8px 0 14px; color: #fff; font-size: clamp(28px, 4vw, 40px); }
.education-heading p { color: rgba(255, 255, 255, .72); line-height: 1.75; }
.education-kicker { color: #62e6bd; font-size: 13px; font-weight: 800; letter-spacing: .08em; }
.education-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.education-card { padding: 24px; border: 1px solid rgba(255, 255, 255, .1); border-radius: 18px; background: rgba(255, 255, 255, .06); }
.education-card > span { color: #62e6bd; font-size: 12px; font-weight: 800; }
.education-card h3 { margin: 14px 0 10px; color: #fff; font-size: 18px; }
.education-card p { color: rgba(255, 255, 255, .68); font-size: 14px; line-height: 1.7; }
.education-related { display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-top: 24px; padding: 24px; border-radius: 18px; background: rgba(255, 255, 255, .08); }
.education-related h3 { margin-bottom: 8px; color: #fff; }
.education-related p { max-width: 700px; color: rgba(255, 255, 255, .7); line-height: 1.7; }
.education-related .btn { flex-shrink: 0; border-color: rgba(255, 255, 255, .28); color: #fff; }
.use-case-section { padding: 72px 0 16px; }
.use-case-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.use-case-card { padding: 26px; border: 1px solid #dfe7eb; border-radius: 18px; background: #fff; box-shadow: 0 12px 30px rgba(35, 55, 68, .05); }
.use-case-card > span { color: #07977d; font-size: 12px; font-weight: 800; }
.use-case-card h3 { margin: 14px 0 10px; font-size: 18px; }
.use-case-card p { color: var(--text-secondary); line-height: 1.7; }
.faq-section { padding: 64px; border: 1px solid #dfe7eb; border-radius: 28px; background: #fff; }
.faq-list { max-width: 860px; margin: 0 auto; }
.faq-item { margin-bottom: 10px; padding: 0 22px; border: 1px solid #e7edf1; border-radius: 15px; background: #fbfcfd; }
.faq-item summary { cursor: pointer; padding: 20px 0; font-weight: 750; }
.faq-item p { padding: 0 0 20px; color: var(--text-secondary); line-height: 1.7; }
.steps { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; }
.step { text-align: center; }
.step-number { width: 40px; height: 40px; border-radius: 50%; background: var(--accent); color: #000; display: flex; align-items: center; justify-content: center; font-weight: 700; margin: 0 auto 12px; }
.step h3 { margin-bottom: 6px; }
.step p { font-size: 13px; color: var(--text-muted); }
.spinner { animation: spin 1s linear infinite; }
.tool-detail-page { padding-top: 0; background: #fff; overflow-x: hidden; }
.first-screen { display: flex; flex-direction: column; width: calc(100vw - 72px); min-height: 100vh; box-sizing: border-box; margin-left: calc(50% - 50vw + 36px); padding: 24px 38px 38px; background: radial-gradient(circle at 82% 8%, rgba(45, 212, 191, .12), transparent 30%), #f7f9fc; }
.first-screen-grid { display: grid; grid-template-columns: minmax(390px, .88fr) minmax(500px, 1.12fr); align-items: center; gap: 32px; width: 100%; max-width: 1180px; margin: 0 auto; flex: 1; }
.action-column { min-width: 0; }
.breadcrumb { width: 100%; max-width: 1180px; margin: 0 auto 18px; padding-top: 24px; }
.tool-hero { margin-bottom: 34px; }
.tool-hero-info { max-width: 820px; }
.tool-icon-lg { display: none; }
.tool-hero-info h1 { font-size: 42px; }
.action-column .tool-interface,
.action-column .login-prompt { width: 100%; max-width: none; min-height: 600px; box-sizing: border-box; margin: 0; border: 1px solid #dfe7eb; border-radius: 18px; background: rgba(255, 255, 255, .95); box-shadow: 0 14px 36px rgba(35, 55, 68, .07); }
.action-column .tool-interface { display: flex; flex-direction: column; padding: 20px; }
.workspace-panel { align-self: center; width: 100%; min-height: 460px; box-sizing: border-box; margin: 0; padding: 18px; border: 1px solid #dfe7eb; border-radius: 18px; background: #fff; box-shadow: 0 14px 36px rgba(35, 55, 68, .07); }
.demo-comparison { flex-wrap: nowrap; }
.demo-item { max-width: none; min-width: 0; }
.demo-image { height: 390px; aspect-ratio: auto; }
.workspace-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.workspace-heading h2 { margin: 0; font-size: 16px; }
.workspace-heading span { display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px; border-radius: 999px; background: #eaf8f3; color: #087d68; font-size: 11px; font-weight: 700; }
.workspace-heading i { width: 7px; height: 7px; border-radius: 50%; background: #10b981; animation: pulse 1.2s ease-in-out infinite; }
.processing-preview { position: relative; height: 370px; overflow: hidden; border-radius: 14px; background: #eef2f4; }
.processing-preview > img { width: 100%; height: 100%; object-fit: contain; }
.processing-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; background: rgba(12, 22, 28, .52); color: #fff; backdrop-filter: blur(2px); }
.processing-overlay .loading-spinner { border-color: rgba(255, 255, 255, .26); border-top-color: #63e6be; }
.processing-track { height: 5px; margin-top: 14px; overflow: hidden; border-radius: 999px; background: #e8edef; }
.processing-track span { display: block; width: 42%; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #10b981, #2dd4bf); animation: processing-slide 1.4s ease-in-out infinite; }
.result-panel .output-image { height: 350px; aspect-ratio: auto; }
.result-panel .output-comparison { gap: 12px; }
.result-panel .output-actions { justify-content: flex-end; margin-top: 14px; }
@keyframes pulse { 50% { opacity: .42; } }
@keyframes processing-slide { 0% { transform: translateX(-110%); } 100% { transform: translateX(340%); } }
@media (max-width: 768px) {
  .first-screen { width: 100%; min-height: 100vh; margin-left: 0; padding: 12px 0 0; }
  .first-screen-grid { grid-template-columns: 1fr; }
  .action-column .login-prompt,
  .action-column .tool-interface,
  .workspace-panel { min-height: 0; margin-bottom: 24px; }
  .steps { grid-template-columns: repeat(2, 1fr); }
  .output-comparison { grid-template-columns: 1fr; }
  .option-controls { align-items: stretch; }
  .feature-row { grid-template-columns: 1fr; gap: 28px; }
  .feature-row:nth-child(even) .feature-visual,
  .feature-row:nth-child(even) .feature-copy { order: initial; }
  .feature-visual { height: 300px; }
  .education-section { margin-top: 64px; padding: 42px 20px; }
  .education-grid { grid-template-columns: 1fr; }
  .education-related { align-items: stretch; flex-direction: column; }
  .education-related .btn { width: 100%; justify-content: center; }
  .use-case-grid { grid-template-columns: 1fr; }
  .faq-section { padding: 48px 20px; }
}

/* 与核心图片工具统一：首屏独立展示，长内容从下一屏开始。 */
.pollo-layout { padding-bottom: 64px; background: #fff; }
.pollo-layout .first-screen { position: relative; width: calc(100vw - 48px); min-height: calc(100vh - 24px); margin-left: calc(50% - 50vw + 24px); padding: 26px 32px 30px; }
.pollo-layout .first-screen .breadcrumb { position: absolute; z-index: 3; top: 42px; left: 48px; width: 410px; margin: 0; padding: 0; }
.pollo-layout .first-screen .tool-hero { width: 100%; margin: 0; }
.pollo-layout .first-screen .tool-hero-info { max-width: 760px; margin: 0 auto; text-align: center; }
.pollo-layout .first-screen .tool-hero-info h1 { font-size: 36px; line-height: 1.32; }
.pollo-layout .first-screen-grid { grid-template-columns: 468px minmax(0, 1fr); align-items: stretch; gap: 72px; width: 100%; max-width: 1392px; min-height: calc(100vh - 84px); }
.pollo-layout .first-screen .showcase-column { display: flex; min-width: 0; flex-direction: column; justify-content: center; gap: 28px; }
.pollo-layout .first-screen .action-column { display: flex; min-height: 0; box-sizing: border-box; padding-top: 54px; }
.pollo-layout .first-screen .tool-interface,
.pollo-layout .first-screen .login-prompt { width: 100%; min-height: 0; margin: 0; padding: 82px 20px 20px; border-radius: 16px; }
.pollo-layout .first-screen .login-prompt { display: flex; align-items: center; justify-content: center; }
.pollo-layout .first-screen .tool-interface { display: flex; height: auto; flex-direction: column; }
.pollo-layout .first-screen .tool-options { margin-top: auto; }
.pollo-layout .first-screen .tool-actions .btn { width: 100%; }
.pollo-layout .first-screen .showcase-column > .workspace-panel { width: min(710px, 100%); min-height: 0; margin: 0 auto; }
.pollo-layout .first-screen .demo-comparison { height: 406px; flex-wrap: nowrap; }
.pollo-layout .first-screen .demo-image { height: 100%; aspect-ratio: auto; }
.pollo-layout .seo-content-start { margin-top: 0; padding-top: 112px; }
.pollo-layout .detail-section { max-width: 1180px; margin-right: auto; margin-bottom: 112px; margin-left: auto; }
.pollo-layout .section-heading { max-width: 800px; margin-bottom: 30px; }
.pollo-layout .section-heading h2,
.pollo-layout .detail-section > h2 { font-size: 40px; line-height: 1.4; }
.section-primary-cta { display: table; min-width: 190px; margin: 0 auto 48px; padding: 13px 28px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 15px; font-weight: 750; text-align: center; }
.section-primary-cta:hover { color: #fff; background: #0a9c82; }
.landing-demo-comparison { position: relative; display: grid; grid-template-columns: 1fr 1fr; width: min(880px, 100%); height: 500px; margin: 0 auto 112px; overflow: hidden; border-radius: 18px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.landing-demo-half { min-width: 0; overflow: hidden; }
.landing-demo-half img { width: 100%; height: 100%; object-fit: cover; }
.landing-demo-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .94); transform: translateX(-1px); }
.pollo-layout .feature-list { gap: 96px; }
.pollo-layout .feature-row { grid-template-columns: minmax(0, 480px) minmax(320px, 480px); justify-content: space-between; gap: 72px; }
.pollo-layout .feature-visual { height: 320px; border-radius: 16px; }
.pollo-layout .feature-copy h3 { font-size: 40px; line-height: 1.4; }
.pollo-layout .education-section { margin-top: 0; }
.pollo-layout .steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; }
.pollo-layout .step { min-height: 142px; padding: 30px; border-radius: 20px; background: #fff; box-shadow: 0 14px 40px rgba(29, 50, 63, .07); }
.pollo-layout .more-tools-section > h2 { margin-bottom: 44px; text-align: center; }
.pollo-layout .more-tools-section .grid-5 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.pollo-layout .more-tools-section .grid-5 > :nth-child(n + 5) { display: none; }
.pollo-layout .faq-section { max-width: 960px; margin-bottom: 48px; padding: 74px 36px; border: 0; }
.pollo-layout .faq-section .section-heading { margin-right: auto; margin-left: auto; text-align: center; }
.pollo-layout .faq-item { margin: 0; padding: 0 8px; border-width: 0 0 1px; border-radius: 0; background: transparent; }
.pollo-layout .faq-item summary { padding: 23px 28px; }
.pollo-layout .faq-item p { padding: 0 28px 22px; }
.pollo-layout .tool-bottom-how-to { margin-top: 0; }

@media (max-width: 1100px) {
  .pollo-layout .first-screen { min-height: auto; }
  .pollo-layout .first-screen .breadcrumb,
  .pollo-layout .first-screen .tool-hero { position: static; width: auto; margin: 0 auto 24px; }
  .pollo-layout .first-screen-grid { grid-template-columns: 1fr; max-width: 760px; min-height: 0; }
  .pollo-layout .first-screen .action-column { padding-top: 0; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { padding-top: 24px; }
  .pollo-layout .first-screen .showcase-column > .workspace-panel { margin-top: 0; }
}

@media (max-width: 768px) {
  .pollo-layout { padding-bottom: 32px; }
  .pollo-layout .first-screen { width: 100vw; margin-left: calc(50% - 50vw); padding: 20px 16px 30px; }
  .pollo-layout .first-screen .showcase-column { display: none; }
  .pollo-layout .first-screen .breadcrumb { margin-bottom: 16px; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { min-height: 610px; padding: 20px 12px; }
  .pollo-layout .seo-content-start { padding-top: 76px; }
  .pollo-layout .detail-section { margin-bottom: 76px; }
  .pollo-layout .section-heading h2,
  .pollo-layout .detail-section > h2 { font-size: 28px; }
  .landing-demo-comparison { height: 320px; margin-bottom: 72px; }
  .pollo-layout .feature-row { gap: 22px; text-align: center; }
  .pollo-layout .feature-visual,
  .pollo-layout .feature-row:nth-child(even) .feature-visual { height: 320px; order: 1; }
  .pollo-layout .feature-copy,
  .pollo-layout .feature-row:nth-child(even) .feature-copy { max-width: none; order: 2; }
  .pollo-layout .feature-copy h3 { font-size: 24px; }
  .pollo-layout .steps { grid-template-columns: 1fr; }
  .pollo-layout .more-tools-section .grid-5 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .pollo-layout .faq-section { margin-bottom: 32px; padding: 48px 0; }
}
</style>
